/**
 * Sparkline — v3.2.45
 *
 * Tiny SVG line chart. No dependencies, dark-theme friendly. Values
 * are auto-scaled to the container. Empty/flat inputs render a
 * baseline dash so admins can still see "no activity" clearly.
 *
 * Usage: <Sparkline data={[3,4,5,2,7]} width={220} height={44} />
 */
export default function Sparkline({
  data = [],
  width = 200,
  height = 40,
  stroke = "#00E5FF",
  fill = "rgba(0,229,255,0.12)",
  labels = null,   // optional string[] parallel to data — used for tooltip titles
}) {
  const w = Number(width) || 200;
  const h = Number(height) || 40;
  const pad = 2;
  const values = data.map((v) => Number(v) || 0);
  const max = Math.max(1, ...values);
  const min = 0;
  const n = values.length;

  if (n < 2) {
    return (
      <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} className="block">
        <line x1={pad} y1={h / 2} x2={w - pad} y2={h / 2}
              stroke="rgba(255,255,255,0.15)" strokeDasharray="3 4" />
      </svg>
    );
  }

  const xStep = (w - pad * 2) / (n - 1);
  const yScale = (v) => {
    const t = max === min ? 0 : (v - min) / (max - min);
    return h - pad - t * (h - pad * 2);
  };

  const points = values.map((v, i) => `${pad + i * xStep},${yScale(v)}`).join(" ");
  const areaPath =
    `M ${pad},${h - pad} ` +
    values.map((v, i) => `L ${pad + i * xStep},${yScale(v)}`).join(" ") +
    ` L ${pad + (n - 1) * xStep},${h - pad} Z`;

  // Peak marker — highlight the highest bar so admins see the spike day.
  const peakIdx = values.indexOf(max);

  return (
    <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} className="block">
      <path d={areaPath} fill={fill} />
      <polyline points={points} fill="none" stroke={stroke} strokeWidth={1.5} />
      {values.map((v, i) => {
        const cx = pad + i * xStep;
        const cy = yScale(v);
        const isPeak = i === peakIdx && v > 0;
        return (
          <g key={i}>
            {isPeak && (
              <circle cx={cx} cy={cy} r={2.5} fill={stroke} />
            )}
            {/* Invisible larger hit-circle for hover tooltip. */}
            <circle cx={cx} cy={cy} r={Math.max(4, xStep / 2)}
                    fill="transparent">
              <title>
                {labels ? labels[i] + " · " : ""}{v} visits
              </title>
            </circle>
          </g>
        );
      })}
    </svg>
  );
}
